import { createSelector, createFeatureSelector } from '@ngrx/store';
import { Params } from "@angular/router";
import { compareConsolidation } from 'src/app/modules/audits/utils/report.utils';
import { calculateCheckProductionTimes, isSubwayCustomer } from 'src/app/modules/admin/utils/subway.utils';
import { ReportId } from 'src/app/modules/report-components/models/ReportId';
import { WaitTimesComposition } from 'src/app/modules/report-components/models/PeopleCounter';
import { PosCategorySopDialogData } from 'src/app/modules/customer-shared/components/pos-category-sop-dialog/pos-category-sop-dialog.component';
import { ReportPageState } from '.';
import { selectMergedRouteParams, selectQueryParams } from '../router/selectors';
import { reportId } from '../_utils/report.utils';
import { selectAllAreas, selectAvailableLocations } from '../auth';
import dayjs from 'dayjs';

export const selectReportState = createFeatureSelector<ReportPageState>('report');

export const selectCustomParams = createSelector(
  selectQueryParams,
  (params: Params | undefined) => {
    const p = params ?? {};

    const sectionIdNum = Number(p['bar']);
    const sopPreNum = Number(p['sopPre']);
    const sopPostNum = Number(p['sopPost']);

    return {
      sectionId: !isNaN(sectionIdNum) ? sectionIdNum : -1,
      employee: p['emp'] != null ? String(p['emp']) : undefined,
      category: p['cat'] != null ? String(p['cat']) : undefined,
      sop:
        !isNaN(sopPreNum) && !isNaN(sopPostNum)
          ? {
            sopPre: sopPreNum,
            sopPost: sopPostNum,
          }
          : undefined,
    };
  }
);

export const selectReportId = createSelector(selectMergedRouteParams, selectAvailableLocations, (params, locations) => {
  const location = locations.find(loc => loc.children.find(a => a.id === +params.areaId));
  const area = location ? location.children.find(a => a.id === +params.areaId) : null;
  const reportId: ReportId = {
    areaId: +params.areaId,
    periodId: params.periodId,
    period: params.periodId ? (params.periodId.length > 7 ? 1 : 2) : 1,
    area: area ? area.name : '',
    location: location ? location.name : '',
    locationId: location ? location.id : 0,
    customerId: location ? location.customerId : 0,
  };
  return reportId;
});

export const selectReport = createSelector(selectReportState, state => state.report);

export const selectCachedReport = createSelector(
  selectMergedRouteParams,
  selectReportState,
  (params, state) => state.cache[reportId(params.areaId, params.periodId)],
);

export const selectCachedDates = createSelector(selectMergedRouteParams, selectReportState, (params, state) => ({
  areaId: params.areaId,
  dates: state.dates[params.areaId],
}));

export const selectDates = createSelector(selectMergedRouteParams, selectReportState, (params, state) => state.dates[params.areaId]);

export const selectMonths = createSelector(selectMergedRouteParams, selectReportState, (params, state) => state.months[params.areaId]);

export const selectCurrentReport = createSelector(selectReportState, state => state.report);

export const selectLoading = createSelector(selectReportState, state => state.loading);

export const selectError = createSelector(selectReportState, state => state.error);

export const selectConsolidationComparison = createSelector(selectReportState, ({ report }) =>
  report && report.trends && report.trends.length >= 2
    ? compareConsolidation(report.consolidation, report.trends[report.trends.length - 2].consolidation)
    : null,
);

export const selectIsSubwayCustomer = createSelector(selectReport, state =>
  state ? isSubwayCustomer(state.customerId, state.locationId) : false,
);

export const selectProductionStats = createSelector(selectReport, state => (state ? state.productionStats : null));

export const selectUpsellMatrix = createSelector(selectReport, state => (state ? state.upsellStats : null));

export const selectWaitTimesComposition = createSelector(selectReport, selectIsSubwayCustomer, (state, isQsr) => {
  if (!state?.peopleCountStats || !isQsr) return [];
  let productionTimes = calculateCheckProductionTimes(state);

  let exitTimes = state.peopleCountStats.waitTimes
    .filter(x => x.boughtSomething)
    .map(w => ({
      leftAt: dayjs(w.entered).add(w.waitedFor, 's'),
      ...w,
    }));
  let res = [] as WaitTimesComposition[];

  for (let exitTime of exitTimes) {
    let indexOfCheck = productionTimes.findIndex(x => x.date.diff(exitTime.leftAt, 's') < 60);
    let productionTime = productionTimes[indexOfCheck];
    if (!productionTime) {
      continue;
    }
    res.push({
      leftAt: exitTime.leftAt,
      productionTimes: productionTime.pt,
      waitedFor: exitTime.waitedFor,
      id: exitTime.id,
    });

    productionTimes = [...productionTimes.filter((_, i) => i !== indexOfCheck)];
  }
  return res;
});

export const selectPosCategorySopUpdating = createSelector(selectReportState, state => state.posCategorySopDialog.updating);

export const selectPosCategorySopState = createSelector(
  selectReportState,
  selectAllAreas,
  (state, allAreas) =>
    (state.report
      ? {
          ...state.posCategorySopDialog,
          reportSop: state.report.sopSettings,
          locationName: state.report.location,
          areas: allAreas.filter(x => x.locationId === state.report.locationId).map(x => ({ id: x.id, name: x.area })),
        }
      : {}) as PosCategorySopDialogData,
);
